import { Suspense, lazy, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { SEO } from '@/components/SEO';

const TwoPhoneHeroDemo = lazy(() =>
  import('@/components/poc/TwoPhoneHeroDemo').then((m) => ({
    default: m.TwoPhoneHeroDemo,
  })),
);
const EmotionalMomentsDemo = lazy(() =>
  import('@/components/poc/EmotionalMomentsDemo').then((m) => ({
    default: m.EmotionalMomentsDemo,
  })),
);
const TrustLayerDemo = lazy(() =>
  import('@/components/poc/TrustLayerDemo').then((m) => ({
    default: m.TrustLayerDemo,
  })),
);
const ScenarioSwapDemo = lazy(() =>
  import('@/components/poc/ScenarioSwapDemo').then((m) => ({
    default: m.ScenarioSwapDemo,
  })),
);

const DEMOS = [
  {
    key: 'two-phone',
    title: 'Two homes, one source of truth',
    description: "A calendar trade request visibly syncs between two parents' phones.",
    Component: TwoPhoneHeroDemo,
  },
  {
    key: 'moments',
    title: 'Emotional beats',
    description:
      'Temperature Check softening a tense message, receipt scan → expense logged, court-ready export.',
    Component: EmotionalMomentsDemo,
  },
  {
    key: 'trust',
    title: 'Trust signals',
    description: 'Timestamps, unalterable-record chips, and a "ship\'s log" audit tape.',
    Component: TrustLayerDemo,
  },
  {
    key: 'swap',
    title: 'Scenario swap',
    description: 'Same phone frame, swappable scenario scenes.',
    Component: ScenarioSwapDemo,
  },
] as const;

type DemoKey = (typeof DEMOS)[number]['key'];

function PocIndexPage() {
  const [active, setActive] = useState<DemoKey>('two-phone');
  const activeDemo = DEMOS.find((d) => d.key === active) ?? DEMOS[0];
  const ActiveComponent = activeDemo.Component;

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Landing device POCs'
        description='Animated device demo concepts for the Harbor marketing site.'
        path='/poc'
        noindex
      />
      <main className='mx-auto max-w-6xl px-6 pt-24 pb-20'>
        <h1 className='text-4xl font-bold tracking-tight text-gray-900 md:text-5xl'>
          Landing device POCs
        </h1>
        <p className='mt-4 max-w-2xl text-xl leading-relaxed text-gray-600'>
          Animated device demo concepts for the marketing site. Pick a concept below.
        </p>
        <div
          role='tablist'
          aria-label='Device demo concepts'
          className='mt-8 flex flex-wrap gap-2 border-b border-gray-200 pb-4'
        >
          {DEMOS.map((demo) => {
            const isActive = demo.key === active;
            return (
              <button
                key={demo.key}
                role='tab'
                aria-selected={isActive}
                onClick={() => setActive(demo.key)}
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {demo.title}
              </button>
            );
          })}
        </div>
        <p className='mt-3 text-sm leading-relaxed text-gray-500'>
          {activeDemo.description}
        </p>
        <div className='mt-6 min-h-[720px] border-t border-gray-100 pt-8'>
          <Suspense
            fallback={
              <div className='flex min-h-[720px] items-center justify-center text-sm text-gray-400'>
                Loading demo…
              </div>
            }
          >
            {activeDemo.key === 'swap' ? (
              <ScenarioSwapDemo syncUrl={false} />
            ) : (
              <ActiveComponent />
            )}
          </Suspense>
        </div>
      </main>
    </div>
  );
}

export const Route = createFileRoute('/poc/')({
  component: PocIndexPage,
});
